import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Button,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { dashboardService } from '../services/dashboardService';
import { LineChart } from '../components/charts/LineChart';
import { MetricCard } from '../components/dashboard/MetricCard';
import { useWebSocket } from '../services/webSocketService';

const ApiMonitoring: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [apiData, setApiData] = useState<any>({});
  const [timeRange, setTimeRange] = useState('1h');
  const theme = useTheme();
  
  // Use WebSocket for real-time updates
  const { data: apiRealTimeData, isConnected } = useWebSocket<any>('api_health');
  
  useEffect(() => {
    fetchApiData();
    
    // Set up polling for API data
    const interval = setInterval(() => {
      fetchApiData();
    }, 60000); // Refresh every minute
    
    return () => clearInterval(interval);
  }, [timeRange]);
  
  useEffect(() => {
    if (apiRealTimeData) {
      setApiData((prevData: any) => ({
        ...prevData,
        ...apiRealTimeData,
        lastUpdated: new Date()
      }));
      setLastUpdated(new Date());
    }
  }, [apiRealTimeData]);
  
  const fetchApiData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await dashboardService.getApiHealth();
      setApiData(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to fetch API health data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value);
  };
  
  // Sample data for response time history
  const responseTimeData = {
    labels: ['12:00', '12:05', '12:10', '12:15', '12:20', '12:25', '12:30'],
    datasets: [
      {
        label: 'Response Time (ms)',
        data: [135, 142, 156, 148, 132, 128, apiData.response_time || 125],
        borderColor: theme.palette.primary.main,
        fill: true,
      }
    ]
  };
  
  // Sample data for request volume
  const requestVolumeData = {
    labels: ['12:00', '12:05', '12:10', '12:15', '12:20', '12:25', '12:30'],
    datasets: [
      {
        label: 'Requests / Minute',
        data: [120, 134, 142, 125, 138, 145, 133],
        borderColor: theme.palette.secondary.main,
        fill: true,
      }
    ]
  };
  
  // Sample data for error rate
  const errorRateData = {
    labels: ['12:00', '12:05', '12:10', '12:15', '12:20', '12:25', '12:30'],
    datasets: [
      {
        label: 'Error Rate (%)',
        data: [0.3, 0.2, 0.5, 0.4, 0.2, 0.1, apiData.error_rate || 0.2],
        borderColor: theme.palette.error.main,
        fill: true,
      }
    ]
  };
  
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'error':
      case 'unhealthy':
        return 'error';
      default:
        return 'info';
    }
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h5" component="h1">
          API Monitoring
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel id="time-range-label">Time Range</InputLabel>
            <Select
              labelId="time-range-label"
              id="time-range"
              value={timeRange}
              onChange={handleTimeRangeChange}
              label="Time Range"
            >
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="6h">Last 6 Hours</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
            </Select>
          </FormControl>
          
          <Chip 
            label={isConnected ? 'Real-time: Active' : 'Real-time: Inactive'} 
            color={isConnected ? 'success' : 'error'} 
            size="small" 
            sx={{ mr: 2 }} 
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
          
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={fetchApiData}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* API Health Overview Cards */}
        <Grid item xs={12} md={3}>
          <MetricCard 
            title="API Status"
            value={apiData.status === 'healthy' ? 100 : apiData.status === 'degraded' ? 50 : 0}
            unit="%"
            status={getStatusColor(apiData.status || 'healthy')}
            icon="api"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard 
            title="Response Time"
            value={apiData.response_time || 125}
            unit="ms"
            status={
              (apiData.response_time || 125) > 200 
                ? 'error' 
                : (apiData.response_time || 125) > 150 
                  ? 'warning' 
                  : 'success'
            }
            icon="custom"
            customIcon={<RefreshIcon fontSize="large" />}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard 
            title="Error Rate"
            value={(apiData.error_rate || 0.2) * 100}
            unit="%"
            status={
              (apiData.error_rate || 0.2) > 1 
                ? 'error' 
                : (apiData.error_rate || 0.2) > 0.5 
                  ? 'warning' 
                  : 'success'
            }
            icon="custom"
            customIcon={<RefreshIcon fontSize="large" />}
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <MetricCard 
            title="Request Count"
            value={apiData.request_count || 156782}
            unit=""
            status="info"
            icon="network"
          />
        </Grid>
        
        {/* Response Time Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Response Time History</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <LineChart 
                labels={responseTimeData.labels}
                datasets={responseTimeData.datasets}
                height={300}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Error Rate Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Error Rate History</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <LineChart 
                labels={errorRateData.labels}
                datasets={errorRateData.datasets}
                height={300}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Request Volume Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Request Volume</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <LineChart 
                labels={requestVolumeData.labels}
                datasets={requestVolumeData.datasets}
                height={300}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Endpoint Status Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Endpoint Status</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Endpoint</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Response Time</TableCell>
                    <TableCell>Error Rate</TableCell>
                    <TableCell>Request Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(apiData.endpoints || []).map((endpoint: any) => (
                    <TableRow key={endpoint.name}>
                      <TableCell>{endpoint.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={endpoint.status} 
                          color={getStatusColor(endpoint.status)}
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(100, (endpoint.response_time / 3))} 
                              color={
                                endpoint.response_time > 200 
                                  ? 'error' 
                                  : endpoint.response_time > 150 
                                    ? 'warning' 
                                    : 'success'
                              }
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Box sx={{ minWidth: 45 }}>
                            <Typography variant="body2" color="text.secondary">
                              {`${endpoint.response_time}ms`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(endpoint.error_rate * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell>
                        {endpoint.request_count ? endpoint.request_count.toLocaleString() : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Add some mock data if no endpoints are available */}
                  {(!apiData.endpoints || apiData.endpoints.length === 0) && (
                    <>
                      <TableRow>
                        <TableCell>/api/v1/predictions</TableCell>
                        <TableCell>
                          <Chip label="healthy" color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={48} 
                                color="success"
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 45 }}>
                              <Typography variant="body2" color="text.secondary">
                                145ms
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>0.1%</TableCell>
                        <TableCell>45,328</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>/api/v1/matches</TableCell>
                        <TableCell>
                          <Chip label="healthy" color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={37} 
                                color="success"
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 45 }}>
                              <Typography variant="body2" color="text.secondary">
                                112ms
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>0.0%</TableCell>
                        <TableCell>36,742</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>/api/v1/odds</TableCell>
                        <TableCell>
                          <Chip label="degraded" color="warning" size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={95} 
                                color="warning"
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 45 }}>
                              <Typography variant="body2" color="text.secondary">
                                287ms
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>0.8%</TableCell>
                        <TableCell>28,931</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>/api/v1/users</TableCell>
                        <TableCell>
                          <Chip label="healthy" color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={33} 
                                color="success"
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 45 }}>
                              <Typography variant="body2" color="text.secondary">
                                98ms
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>0.1%</TableCell>
                        <TableCell>12,498</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiMonitoring;
